/**
 * CSV in and out: the exports (T1.10) and the Phase 0 roster import (T1.07).
 * RFC 4180: comma separated, CRLF or LF rows, double quotes around fields that
 * need them, "" for a literal quote. UTF-8 BOM accepted on input, written on
 * output so Excel opens accented species names correctly.
 */
import { TAG_PATTERN } from './constants';
import type { SpeciesRow, ZoneRow } from './types';

export const CSV_BOM = '﻿';

export function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = Array.isArray(v) ? v.join('|') : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv<T extends object>(rows: readonly T[], columns: readonly (keyof T & string)[]): string {
  const head = columns.map(csvEscape).join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape((r as Record<string, unknown>)[c])).join(','));
  return CSV_BOM + [head, ...body].join('\r\n') + '\r\n';
}

/** Parse CSV text into rows of strings. The first row is the header. */
export function parseCsv(text: string): { header: string[]; rows: string[][] } {
  const src = text.startsWith(CSV_BOM) ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else field += ch;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  const nonEmpty = rows.filter((r) => r.some((c) => c.trim() !== ''));
  const [header = [], ...body] = nonEmpty;
  return { header: header.map((h) => h.trim()), rows: body };
}

export interface RosterInput {
  tag: string;
  species: string;
  zone: string;
  lat: string;
  lng: string;
  accuracy_m: string;
  planted_on: string;
  grid_row: string;
  grid_col: string;
  notes: string;
}

/** Header names the Phase 0 sheet may use for each roster column (lower-cased, accents stripped). */
const ROSTER_ALIASES: Record<keyof RosterInput, string[]> = {
  tag: ['tag', 'etiqueta', 'tree id', 'id', 'tree #', 'tree', 'arbol #', 'arbol', 'no.', 'num'],
  species: ['species', 'especie', 'tree name', 'nombre', 'name', 'nombre del arbol'],
  zone: ['zone', 'zona', 'location', 'ubicacion', 'location/zone'],
  lat: ['lat', 'latitude', 'latitud', 'y'],
  lng: ['lng', 'lon', 'long', 'longitude', 'longitud', 'x'],
  accuracy_m: ['accuracy', 'accuracy_m', 'acc', 'precision', 'gps accuracy'],
  planted_on: ['planted', 'planted_on', 'planting date', 'fecha de siembra', 'sembrado', 'date planted'],
  grid_row: ['row', 'fila', 'grid_row'],
  grid_col: ['col', 'column', 'columna', 'grid_col'],
  notes: ['notes', 'note', 'notas', 'observaciones', 'observations'],
};

export interface RosterTree {
  tag: string;
  species_id: string | null;
  species_text: string;
  zone_id: string | null;
  zone_label: string;
  lat: number | null;
  lng: number | null;
  gps_accuracy_m: number | null;
  planted_on: string | null;
  grid_row: number | null;
  grid_col: number | null;
  notes: string | null;
}

export interface RosterIssue {
  line: number;
  tag: string;
  code: 'tag_invalid' | 'tag_duplicate' | 'tag_wrong_project' | 'species_unknown' | 'zone_unknown' | 'latlng_invalid' | 'date_invalid';
  message: string;
}

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

function matchColumns(header: string[]): Partial<Record<keyof RosterInput, number>> {
  const out: Partial<Record<keyof RosterInput, number>> = {};
  const folded = header.map(fold);
  for (const [key, aliases] of Object.entries(ROSTER_ALIASES) as [keyof RosterInput, string[]][]) {
    const idx = folded.findIndex((h) => aliases.includes(h));
    if (idx >= 0) out[key] = idx;
  }
  return out;
}

/** Accepts 2026-11-03, 03/11/2026 (day first, as written in Nicaragua) and 11/3/2026 when unambiguous. */
export function parseDateLoose(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) {
    let d = Number(m[1]);
    let mo = Number(m[2]);
    if (mo > 12 && d <= 12) [d, mo] = [mo, d];
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    return `${m[3]}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return null;
}

/**
 * Turn the Phase 0 sheet into tree rows for one project. Species and zones are
 * matched by name (es, en or scientific, accent-insensitive); unknown ones are
 * reported, never invented. Tags must match the protocol pattern and belong to
 * the project code.
 */
export function parseRoster(
  text: string,
  ctx: {
    projectCode: string;
    species: readonly Pick<SpeciesRow, 'id' | 'name_es' | 'name_en' | 'scientific'>[];
    zones: readonly Pick<ZoneRow, 'id' | 'label'>[];
  },
): { trees: RosterTree[]; issues: RosterIssue[]; columns: Partial<Record<keyof RosterInput, number>> } {
  const { header, rows } = parseCsv(text);
  const cols = matchColumns(header);
  const trees: RosterTree[] = [];
  const issues: RosterIssue[] = [];
  const seen = new Set<string>();
  const speciesIndex = new Map<string, string>();
  for (const s of ctx.species) {
    for (const n of [s.name_es, s.name_en, s.scientific]) if (n) speciesIndex.set(fold(n), s.id);
  }
  const zoneIndex = new Map(ctx.zones.map((z) => [fold(z.label), z.id]));
  const get = (r: string[], k: keyof RosterInput) => (cols[k] == null ? '' : (r[cols[k] as number] ?? '').trim());

  rows.forEach((r, i) => {
    const line = i + 2;
    const tag = get(r, 'tag').toUpperCase().replace(/\s+/g, '');
    if (!TAG_PATTERN.test(tag)) {
      issues.push({ line, tag, code: 'tag_invalid', message: `tag "${tag}" is not SITE-ZONE-NNN` });
      return;
    }
    if (!tag.startsWith(`${ctx.projectCode}-`)) {
      issues.push({ line, tag, code: 'tag_wrong_project', message: `tag ${tag} does not belong to project ${ctx.projectCode}` });
      return;
    }
    if (seen.has(tag)) {
      issues.push({ line, tag, code: 'tag_duplicate', message: `tag ${tag} appears twice` });
      return;
    }
    seen.add(tag);

    const speciesText = get(r, 'species');
    const species_id = speciesText ? (speciesIndex.get(fold(speciesText)) ?? null) : null;
    if (speciesText && !species_id) issues.push({ line, tag, code: 'species_unknown', message: `species "${speciesText}" is not in the species list` });

    const zoneText = get(r, 'zone') || tag.split('-')[1];
    const zone_id = zoneIndex.get(fold(zoneText)) ?? null;
    if (!zone_id) issues.push({ line, tag, code: 'zone_unknown', message: `zone "${zoneText}" is not a zone of this project` });

    const latS = get(r, 'lat');
    const lngS = get(r, 'lng');
    let lat: number | null = null;
    let lng: number | null = null;
    if (latS || lngS) {
      lat = Number(latS);
      lng = Number(lngS);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        issues.push({ line, tag, code: 'latlng_invalid', message: `lat/lng "${latS}", "${lngS}" is not a WGS84 point` });
        lat = null;
        lng = null;
      }
    }
    const plantedS = get(r, 'planted_on');
    const planted_on = parseDateLoose(plantedS);
    if (plantedS && !planted_on) issues.push({ line, tag, code: 'date_invalid', message: `planting date "${plantedS}" not understood` });

    const num = (s: string) => (s === '' || !Number.isFinite(Number(s)) ? null : Number(s));
    trees.push({
      tag,
      species_id,
      species_text: speciesText,
      zone_id,
      zone_label: zoneText,
      lat,
      lng,
      gps_accuracy_m: num(get(r, 'accuracy_m')),
      planted_on,
      grid_row: num(get(r, 'grid_row')),
      grid_col: num(get(r, 'grid_col')),
      notes: get(r, 'notes') || null,
    });
  });
  return { trees, issues, columns: cols };
}

/** Column lists for the exports (T1.10). Excel and QGIS both open these as-is. */
export const EXPORT_COLUMNS = {
  trees: [
    'tag',
    'zone_label',
    'species_en',
    'species_es',
    'status',
    'lat',
    'lng',
    'gps_accuracy_m',
    'grid_row',
    'grid_col',
    'planted_on',
    'last_survey_date',
    'last_condition',
    'last_height_cm',
    'last_girth_basal_cm',
    'last_diameter_basal_cm',
    'last_girth_dbh_cm',
    'last_diameter_dbh_cm',
    'tree_id',
  ] as const,
  measurements: [
    'survey_date',
    'tag',
    'zone_label',
    'species_en',
    'condition',
    'height_cm',
    'height_method',
    'girth_basal_cm',
    'diameter_basal_cm',
    'girth_dbh_cm',
    'diameter_dbh_cm',
    'soil_moisture',
    'soil_temp',
    'chips',
    'note',
    'team_name',
    'device_lat',
    'device_lng',
    'device_accuracy_m',
    'recorded_at',
    'measurement_id',
    'tree_id',
    'survey_id',
  ] as const,
  surveys: [
    'date',
    'zone_label',
    'team_name',
    'members',
    'weather',
    'found',
    'alive',
    'not_found',
    'survival_rate_pct',
    'care_plan',
    'mulch_plan',
    'pest_notes',
    'irrigation_bottles',
    'notes',
    'survey_id',
  ] as const,
  interventions: ['done_at', 'type', 'quantity', 'unit', 'tag', 'zone_label', 'team_name', 'note', 'intervention_id'] as const,
};
